import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { FiEye, FiEyeOff, FiLock, FiMail, FiUser, FiPhone } from 'react-icons/fi';
import toast from 'react-hot-toast';
import styles from './AuthPage.module.css';

export default function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { login }   = useAuth();
  const navigate    = useNavigate();

  const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const { data } = await registerUser({
        name:     form.name,
        email:    form.email,
        phone:    form.phone,
        password: form.password,
      });
      login(data);
      toast.success('Account created! Welcome to BDShop 🎉');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <Link to="/" className={styles.logo}>🛒 BD<strong>Shop</strong></Link>
          <h1 className={styles.heading}>Create Account</h1>
          <p className={styles.subheading}>Join thousands of happy shoppers in Bangladesh</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          {/* Name */}
          <div className="form-group">
            <label className="form-label" htmlFor="reg-name">Full Name</label>
            <div className={styles.inputWrap}>
              <div className={styles.inputIcon}><FiUser size={15} /></div>
              <input
                id="reg-name" type="text"
                className={`form-input ${styles.inputWithIcon}`}
                value={form.name} onChange={set('name')}
                placeholder="Rahim Uddin" autoComplete="name" required
              />
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label" htmlFor="reg-email">Email Address</label>
            <div className={styles.inputWrap}>
              <div className={styles.inputIcon}><FiMail size={15} /></div>
              <input
                id="reg-email" type="email"
                className={`form-input ${styles.inputWithIcon}`}
                value={form.email} onChange={set('email')}
                placeholder="you@example.com" autoComplete="email" required
              />
            </div>
          </div>

          {/* Phone */}
          <div className="form-group">
            <label className="form-label" htmlFor="reg-phone">Phone Number <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
            <div className={styles.inputWrap}>
              <div className={styles.inputIcon}><FiPhone size={15} /></div>
              <input
                id="reg-phone" type="tel"
                className={`form-input ${styles.inputWithIcon}`}
                value={form.phone} onChange={set('phone')}
                placeholder="01XXXXXXXXX" autoComplete="tel"
              />
            </div>
          </div>

          {/* Password */}
          <div className={styles.formGrid}>
            <div className="form-group">
              <label className="form-label" htmlFor="reg-password">Password</label>
              <div className={styles.inputWrap}>
                <div className={styles.inputIcon}><FiLock size={15} /></div>
                <input
                  id="reg-password" type={showPass ? 'text' : 'password'}
                  className={`form-input ${styles.inputWithIcon}`}
                  value={form.password} onChange={set('password')}
                  placeholder="Min 6 chars" autoComplete="new-password"
                  required minLength={6}
                  style={{ paddingRight: 40 }}
                />
                <button type="button" className={styles.eyeBtn} onClick={() => setShowPass(s => !s)}>
                  {showPass ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-confirm">Confirm</label>
              <div className={styles.inputWrap}>
                <div className={styles.inputIcon}><FiLock size={15} /></div>
                <input
                  id="reg-confirm" type={showConfirm ? 'text' : 'password'}
                  className={`form-input ${styles.inputWithIcon}`}
                  value={form.confirm} onChange={set('confirm')}
                  placeholder="Repeat" autoComplete="new-password"
                  required style={{ paddingRight: 40 }}
                />
                <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirm(s => !s)}>
                  {showConfirm ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              </div>
            </div>
          </div>

          {/* Terms notice */}
          <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            By creating an account you agree to our{' '}
            <Link to="#" style={{ color: 'var(--primary)', fontWeight: 600 }}>Terms of Service</Link>
            {' '}and{' '}
            <Link to="#" style={{ color: 'var(--primary)', fontWeight: 600 }}>Privacy Policy</Link>.
          </p>

          <button
            type="submit"
            className={`btn btn-primary btn-lg btn-block ${styles.submitBtn}`}
            disabled={loading}
          >
            {loading ? (
              <><div className="spinner spinner-sm" /> Creating account…</>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className={styles.dividerRow}>
          <span className={styles.dividerLine} />
          <span className={styles.dividerText}>already have an account?</span>
          <span className={styles.dividerLine} />
        </div>

        <div className={styles.formFooter}>
          <Link to="/login" className="btn btn-outline btn-block">Sign In Instead</Link>
        </div>
      </div>

      <div className={styles.trustBadges}>
        <span>🔒 Secure & Encrypted</span>
        <span>🇧🇩 Bangladesh-based</span>
        <span>📦 Free Delivery on ৳999+</span>
      </div>
    </div>
  );
}
